import {PageHeader} from '../ui/PageHeader';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { useCapability, useStaffSession } from '../ui/StaffSession';
import { Loading, ErrorState } from '../ui/State';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { formatDateOnly } from '../dates';
import './engine-workspaces.css';
type Source = { kind: string; id: string; version: number | null; title: string; revision: string; adoptionRoute: string };
type Meta = {
  applicability: string;
  jurisdiction: string;
  scope: string;
  exclusions: string;
  startsOn: string | null;
  endsOn: string | null;
  reviewOn: string | null;
  reviewRoute: string;
  competenceBasis: string;
  durationMinutes: number | null;
  deadline: string | null;
  requirementReference: string;
};
type Standard = {
  id: string;
  standardId: string;
  version: number;
  service?: string;
  sourceKind?: string;
  sourceId?: string;
  sourceVersion?: number | null;
  sourceTitle?: string;
  sourceHref?: string;
  buildingId?: string | null;
  state: string;
  currentness: string;
  coverage: string;
  metadata?: Meta;
};
type Data = {
  engine: string;
  revision: number;
  ownership: { ownershipStatus: string; primary: { name: string } | null; acting: { name: string; decision: string; availability: string }[] };
  standards: Standard[];
  coverage: string;
  hasMore: boolean;
  canPropose: boolean;
  charter: {
    id: string | null;
    version: number;
    state: string;
    currentness: string;
    instructionVersion: string | null;
    services: string[];
    supported: string[];
    prohibited: string[];
    capabilities: Record<string, string>;
    standardRevisionIds: string[];
  };
};
const blank: Meta = {
  applicability: 'Unknown',
  jurisdiction: '',
  scope: '',
  exclusions: '',
  startsOn: null,
  endsOn: null,
  reviewOn: null,
  reviewRoute: '',
  competenceBasis: '',
  durationMinutes: null,
  deadline: null,
  requirementReference: ''
};
const nullable = (s: FormDataEntryValue | null) => (s ? String(s) : null);
// Project editable fields only: stored metadata also contains server-owned review and target proofs.
const editableMetadata = (value: Meta): Meta => Object.fromEntries(Object.keys(blank).map((key) => [key, value[key as keyof Meta]])) as Meta;
export function EngineStandards() {
  const { code } = useParams(),
    [params] = useSearchParams(),
    building = params.get('building'),
    session = useStaffSession();
  const [data, setData] = useState<Data>(),
    [sources, setSources] = useState<Source[]>([]),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [editing, setEditing] = useState<Standard>(),
    [meta, setMeta] = useState<Meta>(blank),
    [sourceKey, setSourceKey] = useState('');
  const [learning, setLearning] = useState<{ items: { id: string; title: string; stage: string; reviewNeeded: boolean; requirementState: string; sourceHref: string }[] }>();
  const canLearn = useCapability('workforce:view-private') && useCapability('hrb-competence:view');
  const retry = useRef<{ fingerprint: string; key: string } | undefined>(undefined);
  const load = async () => {
    const d = await api<Data>('/api/engine-standards/' + code);
    setData(d);
    if (d.canPropose) setSources((await api<{ sources: Source[] }>('/api/engine-standards/' + code + '/sources')).sources);
    if (canLearn) setLearning(await api('/api/engine-standards/' + code + '/learning'));
  };
  useEffect(() => {
    setData(undefined);
    setError(undefined);
    setEditing(undefined);
    setMeta(blank);
    setSourceKey('');
    void load().catch(setError);
  }, [code]);
  async function save(path: string, payload: Record<string, unknown>) {
    setBusy(true);
    setNotice('');
    setError(undefined);
    try {
      const fingerprint = JSON.stringify(payload);
      if (retry.current?.fingerprint !== fingerprint) retry.current = { fingerprint, key: crypto.randomUUID() };
      await api(path, { method: 'POST', body: JSON.stringify({ ...payload, idempotencyKey: retry.current.key }) });
      retry.current = undefined;
      setNotice('Saved.');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const standardId = useRef(crypto.randomUUID());
  async function submitStandard(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const f = new FormData(e.currentTarget),
      source = sources.find((s) => s.kind + ':' + s.id + ':' + s.version === sourceKey);
    if (!source) return;
    await save('/api/engine-standards', {
      ...editableMetadata(meta),
      engine: code,
      expectedScopeRevision: data.revision,
      basis: f.get('basis'),
      standardId: editing?.standardId ?? standardId.current,
      priorId: editing?.id ?? null,
      service: editing?.service ?? data.charter.services[0],
      sourceKind: source.kind,
      sourceId: source.id,
      sourceVersion: source.version,
      expectedSourceRevision: source.revision,
      buildingId: editing?.buildingId ?? building ?? nullable(f.get('building')),
      state: f.get('state'),
      reviewSource: f.get('state') === 'Adopted' && source.kind === 'ReportTemplate' && f.get('reviewSource') === 'on'
    });
  }
  if (!data) return error ? <ErrorState error={error} /> : <Loading />;
  return (
    <div className="engine-workspaces">
      <PageHeader title="Standards and charter"/>
      <Link to={'/engines/' + code + (building ? '?building=' + building : '')}>Back to engine</Link>
      <p>References to original methods and requirements. Published templates and recorded learning do not certify competence or make work ready to book.</p>
      {notice && <p role="status">{notice}</p>}
      {error !== undefined && <ErrorState error={error} />}
      <section aria-label="Engine charter">
        <h2>Engine charter</h2>
        <p>
          Version {data.charter.version || 'unconfirmed'} · {data.charter.state} · {data.charter.currentness}
        </p>
        <p>
          Engine Manager: {data.ownership.primary?.name ?? data.ownership.ownershipStatus} · {data.ownership.ownershipStatus}
        </p>
        {data.ownership.acting.map((a, i) => (
          <p key={i}>
            Acting: {a.name} · {a.decision === 'engine_technical_review' ? 'Technical review' : 'Engine delivery'} · {a.availability}
          </p>
        ))}
        <p>Supported: {data.charter.supported.join('; ')}.</p>
        <p>Assistance, booking, source completion and tool activation: Unsupported in this foundation.</p>
        <p>Prohibited: {data.charter.prohibited.join('; ')}.</p>
        {data.canPropose && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save('/api/engine-charters', {
                engine: code,
                expectedScopeRevision: data.revision,
                priorId: data.charter.id,
                state: f.get('charterState'),
                instructionVersion: f.get('instructionVersion'),
                standardRevisionIds: f.getAll('standard'),
                basis: f.get('basis')
              });
            }}
          >
            <label>
              Instruction version <input name="instructionVersion" required maxLength={80} defaultValue={data.charter.instructionVersion ?? ''} />
            </label>
            <fieldset>
              <legend>Referenced standards</legend>
              {data.standards
                .filter((s) => s.metadata)
                .map((s) => (
                  <label key={s.id}>
                    <input type="checkbox" name="standard" value={s.id} defaultChecked={data.charter.standardRevisionIds.includes(s.id)} />
                    {s.sourceTitle} · revision {s.version} · {s.currentness}
                  </label>
                ))}
            </fieldset>
            <label>
              Charter status{' '}
              <select name="charterState" defaultValue="Draft">
                <option>Draft</option>
                <option>Review requested</option>
                <option>Adopted</option>
                <option>Retired</option>
              </select>
            </label>
            <label>
              Charter decision basis <input name="basis" required minLength={3} maxLength={500} />
            </label>
            <button disabled={busy}>Save charter</button>
            <p>Adoption requires current engine technical responsibility, policy authority and current adopted standards. This does not activate tools or change queue modes.</p>
          </form>
        )}
      </section>
      <section aria-label="Method references">
        <h2>Method and requirement references</h2>
        <p>
          Coverage: {data.coverage}
          {data.hasMore ? ' · latest 100 revisions shown' : ''}
        </p>
        {!data.standards.length && <p>No permitted references recorded.</p>}
        <ul>
          {data.standards.map((s) => (
            <li key={s.id}>
              {s.sourceTitle ?? 'Source unavailable'} · revision {s.version} · {s.state} · {s.currentness}
              {s.metadata && (
                <>
                  <p>
                    {s.metadata.scope} · {s.metadata.jurisdiction} · applicability {s.metadata.applicability}
                  </p>
                  <p>
                    Review {s.metadata.reviewOn ? formatDateOnly(s.metadata.reviewOn) : 'unconfirmed'}; duration {s.metadata.durationMinutes ?? 'unconfirmed'}; deadline{' '}
                    {s.metadata.deadline ? formatDateOnly(s.metadata.deadline) : 'unset'}
                  </p>
                  <Link to={s.sourceHref!}>Open original source</Link>
                  {data.canPropose && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(s);
                        setMeta(editableMetadata(s.metadata!));
                        setSourceKey(s.sourceKind + ':' + s.sourceId + ':' + s.sourceVersion);
                        setNotice('Editing a new revision; history is retained.');
                      }}
                    >
                      Revise reference {s.version}
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
      {data.canPropose && (
        <section>
          <h2>{editing ? 'Revise reference' : 'Add method reference'}</h2>
          <p>Saving requires current engine delivery or technical responsibility and the original source permissions. Sources without technical adoption remain pending review.</p>
          <form onSubmit={(e) => void submitStandard(e)}>
            <label>
              Original method source{' '}
              <select required value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
                <option value="">Choose an existing source</option>
                {sources.map((s) => (
                  <option key={s.kind + ':' + s.id} value={s.kind + ':' + s.id + ':' + s.version}>
                    {s.title} · {s.kind} {s.version ?? ''}
                  </option>
                ))}
              </select>
            </label>
            {!building && !editing && (
              <label>
                Building target{' '}
                <select name="building">
                  <option value="">Service reference; no building commissioned</option>
                  {session?.buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Applicability{' '}
              <select value={meta.applicability} onChange={(e) => setMeta({ ...meta, applicability: e.target.value })}>
                <option>Unknown</option>
                <option>Confirmed</option>
                <option>Not applicable</option>
              </select>
            </label>
            {(['jurisdiction', 'scope', 'exclusions', 'reviewRoute', 'competenceBasis', 'requirementReference'] as const).map((key) => (
              <label key={key}>
                {
                  {
                    jurisdiction: 'Jurisdiction',
                    scope: 'Applicable scope',
                    exclusions: 'Exclusions',
                    reviewRoute: 'Technical review route',
                    competenceBasis: 'Competence requirement basis',
                    requirementReference: 'Original requirement reference'
                  }[key]
                }{' '}
                <input required={key !== 'exclusions'} value={meta[key]} maxLength={500} onChange={(e) => setMeta({ ...meta, [key]: e.target.value })} />
              </label>
            ))}
            {(['startsOn', 'endsOn', 'reviewOn', 'deadline'] as const).map((key) => (
              <label key={key}>
                {{ startsOn: 'Effective from', endsOn: 'Effective until, exclusive', reviewOn: 'Review due', deadline: 'Source deadline, if known' }[key]}{' '}
                <DateOnlyInput value={meta[key] ?? ''} onChange={(v) => setMeta({ ...meta, [key]: v || null })} />
              </label>
            ))}
            <label>
              Expected duration in minutes, if commissioned{' '}
              <input
                type="number"
                min={1}
                max={1440}
                value={meta.durationMinutes ?? ''}
                onChange={(e) => setMeta({ ...meta, durationMinutes: e.target.value ? Number(e.target.value) : null })}
              />
            </label>
            <label>
              Reference status{' '}
              <select name="state" defaultValue="Draft">
                <option>Draft</option>
                <option>Review requested</option>
                <option>Adopted</option>
                <option>Retired</option>
              </select>
            </label>
            <label>
              Reference decision basis <input name="basis" required minLength={3} maxLength={500} />
            </label>
            {sources.find((s) => s.kind + ':' + s.id + ':' + s.version === sourceKey)?.kind === 'ReportTemplate' && (
              <label key={sourceKey + ':' + (editing?.id ?? 'new')}>
                <input type="checkbox" name="reviewSource" />
                Record my technical review of this template version, scope and exclusions when adopting this reference
                <p>Requires current engine technical responsibility. The review is retained with your identity and decision basis. It does not certify an inspector, approve spending or book work.</p>
              </label>
            )}
            <button disabled={busy || !sourceKey}>Save reference</button>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  setEditing(undefined);
                  setMeta(blank);
                  standardId.current = crypto.randomUUID();
                  setSourceKey('');
                }}
              >
                Add a different reference
              </button>
            )}
          </form>
        </section>
      )}
      {canLearn && (
        <section aria-label="Private learning links">
          <h2>Private learning links</h2>
          <p>
            Planned, attended and reviewed learning are distinct. An exact activity-specific workforce requirement check is still needed; no professional certification is implied.
          </p>
          <ul>
            {learning?.items.map((l) => (
              <li key={l.id}>
                {l.title} · {l.stage} · {l.reviewNeeded ? 'Review needed' : 'Recorded'} · {l.requirementState} <Link to={l.sourceHref}>Open original evidence profile</Link>
              </li>
            ))}
          </ul>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save('/api/engine-learning', {
                engine: code,
                expectedScopeRevision: data.revision,
                service: data.charter.services[0],
                standardRevisionId: nullable(f.get('standardId')),
                itemId: f.get('itemId'),
                reviewId: nullable(f.get('reviewId')),
                buildingId: building ?? f.get('building'),
                basis: f.get('basis')
              });
            }}
          >
            <label>
              Existing learning item reference <input name="itemId" required />
            </label>
            <label>
              Existing profile review reference, if reviewed <input name="reviewId" />
            </label>
            <label>
              Standard revision{' '}
              <select name="standardId">
                <option value="">No standard link</option>
                {data.standards.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.sourceTitle} · revision {s.version}
                  </option>
                ))}
              </select>
            </label>
            {!building && (
              <label>
                Evidence building{' '}
                <select name="building" required>
                  <option value="">Choose an authorised building</option>
                  {session?.buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Learning association basis <input name="basis" required minLength={3} maxLength={500} />
            </label>
            <button disabled={busy}>Link existing learning evidence</button>
          </form>
        </section>
      )}
    </div>
  );
}
